import { useEffect, useMemo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Stack,
  Chip,
  Box,
  Divider,
} from "@mui/material";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import LightbulbOutlinedIcon from "@mui/icons-material/LightbulbOutlined";
import { canalLabel } from "./pipelineUtils";

const ICON_MAP = {
  warning: <WarningAmberIcon color="warning" />,
  error: <ErrorOutlineIcon color="error" />,
  success: <CheckCircleOutlineIcon color="success" />,
};

const bgcolorTipo = (tipo) =>
  tipo === "error" ? "tint.danger" : tipo === "warning" ? "tint.warning" : "tint.revenue";

function FilaAlerta({ alerta, pendiente, onLeida }) {
  return (
    <Box
      sx={{
        display: "flex",
        gap: 1.5,
        alignItems: "flex-start",
        p: 1.5,
        borderRadius: 1,
        bgcolor: bgcolorTipo(alerta.tipo),
        opacity: pendiente ? 0.65 : 1,
      }}
    >
      {ICON_MAP[alerta.tipo] || <WarningAmberIcon color="warning" />}
      <Typography variant="body2" sx={{ flex: 1 }}>{alerta.mensaje}</Typography>
      {pendiente ? (
        <Chip label="Pendiente" size="small" variant="outlined" />
      ) : (
        <Button size="small" onClick={() => onLeida(alerta.id)}>Leída</Button>
      )}
    </Box>
  );
}

export default function AlertasInteligentes({ open, onClose, store, subs }) {
  const todasAlertas = useMemo(
    () => [...store.alertas, ...(subs?.alertasSuscripcion || [])],
    [store.alertas, subs?.alertasSuscripcion]
  );
  const leidas = useMemo(() => new Set(store.config.alertasLeidas || []), [store.config.alertasLeidas]);
  const nuevas = todasAlertas.filter((a) => a.id && !leidas.has(a.id));
  const pendientes = todasAlertas.filter((a) => a.id && leidas.has(a.id));

  const pruneAlertasLeidas = store.pruneAlertasLeidas;
  const idsKey = todasAlertas.map((a) => a.id).filter(Boolean).join(",");

  useEffect(() => {
    if (!open) return;
    pruneAlertasLeidas(idsKey ? idsKey.split(",") : []);
  }, [open, idsKey, pruneAlertasLeidas]);

  const sugerencias = [];

  if (store.leadsConScore.length > 0) {
    const top = store.leadsConScore[0];
    sugerencias.push(`Contacta hoy a ${top.nombre} (prioridad ${top.score}/100): requiere seguimiento.`);
  }

  if (store.ventasPorCanal.length > 0) {
    const mejor = store.ventasPorCanal[0];
    sugerencias.push(`Tu mejor canal es ${canalLabel(mejor.canal)} con ${mejor.cantidad} ventas y ${store.config.moneda} ${mejor.total.toLocaleString()} en ingresos.`);
  }

  if (store.tasaConversion > 0) {
    sugerencias.push(`Tu tasa de conversión actual es ${store.tasaConversion}%. ${store.tasaConversion < 30 ? "Enfócate en seguimiento para mejorarla." : "¡Buen trabajo!"}`);
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle fontWeight={700}>
        Alertas y seguimiento
        {nuevas.length > 0 && (
          <Chip label={nuevas.length} color="error" size="small" sx={{ ml: 1 }} />
        )}
        {pendientes.length > 0 && (
          <Chip label={`${pendientes.length} pendiente${pendientes.length === 1 ? "" : "s"}`} size="small" variant="outlined" sx={{ ml: 1 }} />
        )}
      </DialogTitle>
      <DialogContent>
        {todasAlertas.length === 0 ? (
          <Box textAlign="center" py={3}>
            <CheckCircleOutlineIcon sx={{ fontSize: 48, color: "success.main" }} />
            <Typography color="text.secondary" mt={1}>
              Todo en orden. No hay alertas pendientes.
            </Typography>
          </Box>
        ) : (
          <Stack spacing={2} mb={2}>
            {nuevas.length > 0 && (
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Nuevas</Typography>
                <Stack spacing={1}>
                  {nuevas.map((a) => (
                    <FilaAlerta key={a.id} alerta={a} onLeida={store.marcarAlertaLeida} />
                  ))}
                </Stack>
              </Box>
            )}
            {pendientes.length > 0 && (
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Acciones pendientes</Typography>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                  Ya las viste; siguen acá hasta que se resuelva el problema.
                </Typography>
                <Stack spacing={1}>
                  {pendientes.map((a) => (
                    <FilaAlerta key={a.id} alerta={a} pendiente />
                  ))}
                </Stack>
              </Box>
            )}
          </Stack>
        )}

        {sugerencias.length > 0 && (
          <>
            <Divider sx={{ my: 2 }} />
            <Stack direction="row" spacing={0.75} alignItems="center" mb={1}>
              <LightbulbOutlinedIcon fontSize="small" color="info" />
              <Typography variant="subtitle2">Sugerencias</Typography>
            </Stack>
            <Stack spacing={1}>
              {sugerencias.map((s) => (
                <Box
                  key={s}
                  sx={{ display: "flex", gap: 1, alignItems: "flex-start", p: 1, borderRadius: 1, bgcolor: "tint.potential" }}
                >
                  <Typography variant="body2">{s}</Typography>
                </Box>
              ))}
            </Stack>
          </>
        )}
      </DialogContent>
      <DialogActions>
        {nuevas.length > 0 && (
          <Button onClick={() => store.marcarAlertasLeidas(nuevas.map((a) => a.id))}>
            Marcar todas como leídas
          </Button>
        )}
        <Button onClick={onClose}>Cerrar</Button>
      </DialogActions>
    </Dialog>
  );
}
